import React, { useEffect, useMemo, useRef, useState } from "react";
import { createWorkOrder, updateWorkOrder } from "../../api/work-orderAPI";
import { getWorkOrderTasksByAsset } from "../../api/tasksAPI";
import { useFormBuilder } from "../../utils/useFormBuilder";
import { useSetting, useSettings } from "../../utils/AppSettingsContext";
import { useValidator } from "../../utils/useValidator";
import { useUserProfile } from "../../masterLayout/MasterLayoutBody";

const toLocalInputValue = (date) => {
  if (!date) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const toUtcString = (value) => (value ? new Date(value).toISOString() : null);

const HOURS_IN_MS = 60 * 60 * 1000;

const parseDateInput = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const parseDurationInput = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return num;
};

const formatDurationHours = (hours) => {
  if (!Number.isFinite(hours) || hours < 0) return "";
  const rounded = Math.round(hours * 100) / 100;
  return rounded.toFixed(2);
};

const computeDurationFromValues = (startedValue, completedValue) => {
  const start = parseDateInput(startedValue);
  const end = parseDateInput(completedValue);
  if (!start || !end) return "";
  const diffMs = end.getTime() - start.getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return "";
  return formatDurationHours(diffMs / HOURS_IN_MS);
};

const toDurationInputValue = (value) => {
  if (value === undefined || value === null || value === "") return "";
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) return "";
  return formatDurationHours(num);
};

const parseShiftStartTime = (shiftStartSetting) => {
  if (!shiftStartSetting) {
    return { hours: 8, minutes: 0 };
  }

  const match = /^([0-1]?\d|2[0-3]):([0-5]\d)$/.exec(String(shiftStartSetting));
  if (!match) {
    return { hours: 8, minutes: 0 };
  }

  return { hours: Number(match[1]), minutes: Number(match[2]) };
};

const getAssetOption = (component) => {
  const assetId = component?.assetId ?? null;
  if (!assetId) {
    return null;
  }

  const assetNumber = component?.asset?.assetNumber || "";
  const assetDescription = component?.asset?.assetDescription || "";
  const label = [assetNumber, assetDescription].filter(Boolean).join(" - ");
  return {
    value: assetId,
    label: label || "Asset",
  };
};

const getComponentUnitName = (component) =>
  component?.assetMeter?.meter?.measurementUnits?.unitName ||
  "";

const shouldShowComponentStartValue = (component, formData) => {
  const hasAsset = Boolean(formData?.assetId);
  const isComponentTask = Boolean(component?.task?.taskType?.component);
  return hasAsset && isComponentTask;
};

const toLocalInputValueFromString = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return toLocalInputValue(date);
};

const getLinkedTaskTypeId = (component, existingWorkOrder) => {
  const taskTypeIdFromComponent =
    component?.taskTypeId ??
    null;

  if (taskTypeIdFromComponent !== null && taskTypeIdFromComponent !== undefined) {
    return taskTypeIdFromComponent;
  }

  const taskTypeIdFromWorkOrder =
    existingWorkOrder?.taskTypeId ??
    existingWorkOrder?.task?.taskTypeId ??
    null;

  return taskTypeIdFromWorkOrder;
};

const ReplaceComponentWorkOrderModal = ({
  show,
  component,
  onClose,
  onSaved,
  mode = "create",
  existingWorkOrder = null,
}) => {
  const autoGenWorkOrderNo = useSetting("auto_generate_work_order");
  const shiftStartTime = useSettings("shift_start_time");
  const userProfile = useUserProfile();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [assetTaskOptions, setAssetTaskOptions] = useState([]);
  const [selectedTaskOption, setSelectedTaskOption] = useState(null);

  const assetOption = useMemo(() => getAssetOption(component), [component]);
  const componentUnitName = useMemo(() => getComponentUnitName(component), [component]);

  const showStartedAt = [2, 4].includes(Number(formData?.statusId || 0));
  const showCompletedAt = Number(formData?.statusId || 0) === 4;
  const showComponentStart = shouldShowComponentStartValue(component, formData);

  const validationFields = useMemo(() => {
    const base = [
      { field: "workOrderTitle", label: "Title", required: true },
      { field: "assetId", label: "Asset", required: true },
      { field: "statusId", label: "Status", required: true },
      { field: "taskTypeId", label: "Task Type", required: true },
      { field: "taskId", label: "Linked Task", required: true },
    ];

    if (!autoGenWorkOrderNo) {
      base.unshift({ field: "workOrderNumber", label: "Number", required: true });
    }

    if (showStartedAt) {
      base.push({ field: "startedAt", label: "Started", type: "datetime", required: true });
    }

    if (showCompletedAt) {
      base.push({ field: "completedAt", label: "Completed", type: "datetime", required: true });
    }

    return base;
  }, [autoGenWorkOrderNo, showStartedAt, showCompletedAt]);

  const { errors, setErrors, validate } = useValidator(validationFields, formData);

  const workOrderFields = useMemo(() => {
    const fields = [
      ...(!autoGenWorkOrderNo
        ? [
          {
            field: "workOrderNumber",
            label: "Number",
            type: "text",
            size: 4,
            required: true,
          },
        ]
        : []),
      { field: "workOrderTitle", label: "Title", type: "text", size: 6, required: true },
      { field: "statutory", label: "Compliance Task", type: "switch", size: 2, switchColor: "danger" },
      {
        field: "workOrderDescription",
        label: "Description",
        type: "textarea",
        size: 12,
        required: false,
        rows: 3,
      },
      {
        field: "assetId",
        label: "Asset",
        type: "select",
        size: 6,
        required: true,
        options: assetOption ? [assetOption] : [],
        disabled: true,
      },
      {
        field: "statusId",
        label: "Status",
        type: "select",
        size: 6,
        required: true,
        api: "work-order-status",
        disabled: true,
      },
      {
        field: "taskId",
        label: "Linked Task",
        type: "select",
        size: 6,
        required: true,
        options: assetTaskOptions,
        isClearable: true,
        selectedOption: selectedTaskOption,
        onChange: (option) => {
          const task = option?.task ?? null;
          const cycleOrder =
            task?.schedulingType === "cycle" && task?.cycleGroupOrder !== null && task?.cycleGroupOrder !== undefined
              ? task.cycleGroupOrder
              : null;
          setSelectedTaskOption(option ?? null);
          setFormData((prev) => ({
            ...prev,
            taskId: task?.id ?? null,
            cycleGroupOrder: cycleOrder,
          }));
        },
      },
      {
        field: "taskTypeId",
        label: "Task Type",
        type: "select",
        size: 6,
        required: true,
        api: "task-types",
        displayField: 'taskTypeName',
        disabled: false,
      },
    ];

    if (showComponentStart) {
      const labelSuffix = componentUnitName ? ` in ${componentUnitName}` : "";
      fields.push({
        field: "component_start_value",
        label: `Component Starting Value${labelSuffix}`,
        type: "number",
        size: 6,
        required: false,
        min: 0,
        step: 1,
      });
    }

    if (showStartedAt) {
      fields.push({ field: "startedAt", label: "Started", type: "datetime", size: 4, required: true });
      fields.push({
        field: "actualDuration",
        label: "Actual Duration (hrs)",
        type: "number",
        size: 4,
        min: 0,
        step: 0.01,
      });
    }

    if (showCompletedAt) {
      fields.push({ field: "completedAt", label: "Completed", type: "datetime", size: 4, required: true });
    }

    fields.push(
      {
        field: "notes",
        label: "Completion Notes",
        type: "textarea",
        size: 12,
      },
      {
        field: "completionImages",
        label: "Completion Images",
        type: "images",
        size: 12,

      }
    );

    return fields;
  }, [
    autoGenWorkOrderNo,
    assetOption,
    componentUnitName,
    showComponentStart,
    showCompletedAt,
    showStartedAt,
    assetTaskOptions,
    selectedTaskOption,
  ]);

  const { renderField } = useFormBuilder(workOrderFields, formData, setFormData, errors);

  useEffect(() => {
    if (!show || !component) {
      return;
    }

    setErrors({});

    if (mode === "edit" && existingWorkOrder) {
      const assetId = existingWorkOrder?.assetId ?? component?.assetId ?? null;
      const taskId = existingWorkOrder?.taskId ?? component?.task?.id ?? null;
      const statutory = existingWorkOrder?.statutory ? 1 : 0;
      const title = existingWorkOrder?.workOrderTitle || component.name;
      const taskTypeId = getLinkedTaskTypeId(component, existingWorkOrder) ?? 1;

      setFormData({
        workOrderNumber: existingWorkOrder?.workOrderNumber || "",
        workOrderTitle: title,
        statutory,
        workOrderDescription: existingWorkOrder?.workOrderDescription || "",
        assetId,
        statusId: 4,
        taskId,
        cycleGroupOrder: existingWorkOrder?.cycleGroupOrder ?? null,
        taskTypeId,
        priorityId: 3,
        scheduledAt: toLocalInputValueFromString(existingWorkOrder?.scheduledAt),
        dueDate: toLocalInputValueFromString(existingWorkOrder?.dueDate),
        startedAt: toLocalInputValueFromString(existingWorkOrder?.startedAt),
        completedAt: toLocalInputValueFromString(existingWorkOrder?.completedAt),
        actualDuration:
          toDurationInputValue(existingWorkOrder?.actualDuration) ||
          computeDurationFromValues(existingWorkOrder?.startedAt, existingWorkOrder?.completedAt),
        component_start_value:
          existingWorkOrder?.componentStartValue ?? "",
        notes: existingWorkOrder?.notes ?? "",
        completionImages: existingWorkOrder?.completionImages ?? [],
      });
      return;
    }

    const assetId = component?.assetId ?? null;
    const taskId = component?.id ?? null;
      const statutory = component?.statutory ? 1 : 0;
      const title = component?.name || "";
      const taskTypeId = getLinkedTaskTypeId(component, existingWorkOrder) ?? 1;
    const { hours, minutes } = parseShiftStartTime(shiftStartTime);
    const now = new Date();
    const startCandidate = new Date(now);
    startCandidate.setHours(hours, minutes, 0, 0);
    if (now < startCandidate) {
      startCandidate.setDate(startCandidate.getDate() - 1);
    }
    const startedAtValue = toLocalInputValue(startCandidate);

    const eightHoursLater = new Date(startCandidate.getTime() + 8 * HOURS_IN_MS);
    const completedCandidate = eightHoursLater > now ? now : eightHoursLater;
    const defaultDuration = formatDurationHours((completedCandidate.getTime() - startCandidate.getTime()) / HOURS_IN_MS);
    const completedAtValue = toLocalInputValue(completedCandidate);
    const nextTaskId = component.usageOccurrences.find(o => o.workOrderStatusId !== 4)?.taskId ?? null;
    const nextCyclePriority = component.usageOccurrences.find(o => o.workOrderStatusId !== 4)?.cyclePriority ?? null;

    setFormData({
      workOrderNumber: "",
      workOrderTitle: title,
      statutory,
      workOrderDescription: "",
      assetId,
      statusId: 4,
      taskId: nextTaskId,
      cycleGroupOrder: nextCyclePriority,
      taskTypeId,
      priorityId: 3,
      scheduledAt: "",
      dueDate: "",
      startedAt: startedAtValue,
      completedAt: completedAtValue,
      actualDuration: defaultDuration || "",
      component_start_value: "",
    });
  }, [component, existingWorkOrder, mode, shiftStartTime, show]);

  useEffect(() => {
    if (!show) return;
    const assetId = component?.assetId ?? null;
    if (!assetId) {
      setAssetTaskOptions([]);
      setSelectedTaskOption(null);
      setFormData((prev) =>
        prev.taskId || prev.cycleGroupOrder ? { ...prev, taskId: null, cycleGroupOrder: null } : prev,
      );
      return;
    }

    let cancelled = false;

    const loadTasks = async () => {
      try {
        const response = await getWorkOrderTasksByAsset(assetId);
        if (cancelled) return;
        const tasks = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        const options = tasks.map((task) => {
          const baseLabel =
            [task.code?.name ? `${task.code.code} - ${task.name}` : task.name]
              .filter(Boolean)
              .join(" – ") || `Task ${task.id}`;
          const label =
            task.schedulingType === "cycle" && task.cycleGroupOrder !== null && task.cycleGroupOrder !== undefined
              ? `${baseLabel} [${task.cycleGroupOrder}]`
              : baseLabel;

          return {
            value: `${task.id}-${task.cycleGroupOrder ?? "none"}`,
            label,
            task,
          };
        });

        setAssetTaskOptions(options);
        setFormData((prev) => {
          if (
            prev.taskId &&
            !options.some(
              (opt) =>
                Number(opt.task?.id) === Number(prev.taskId) &&
                (opt.task?.schedulingType !== "cycle"
                  ? true
                  : (opt.task?.cycleGroupOrder ?? null) === (prev.cycleGroupOrder ?? null)),
            )
          ) {
            return { ...prev, taskId: null, cycleGroupOrder: null };
          }
          return prev;
        });
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading tasks for asset", err);
        setAssetTaskOptions([]);
        setSelectedTaskOption(null);
      }
    };

    loadTasks();

    return () => {
      cancelled = true;
    };
  }, [component?.assetId, show]);

  useEffect(() => {
    const rawTaskId = formData.taskId;
    const numericTaskId = Number(rawTaskId);
    if (!assetTaskOptions.length || rawTaskId === null || rawTaskId === undefined || rawTaskId === "") {
      if (selectedTaskOption !== null) {
        setSelectedTaskOption(null);
      }
      return;
    }
    if (Number.isNaN(numericTaskId)) {
      return;
    }

    const desiredOrder = formData.cycleGroupOrder ?? null;
    const match = assetTaskOptions.find((opt) => {
      const optionTaskId = Number(opt?.task?.id ?? null);
      if (optionTaskId !== numericTaskId) return false;
      if (opt?.task?.schedulingType === "cycle") {
        return (opt.task?.cycleGroupOrder ?? null) === (desiredOrder ?? null);
      }
      return true;
    });

    if (match || selectedTaskOption !== null) {
      setSelectedTaskOption(match ?? null);
    }
  }, [assetTaskOptions, formData.cycleGroupOrder, formData.taskId, selectedTaskOption]);

  const durationDrivenUpdate = useRef(false);

  useEffect(() => {
    if (!showStartedAt) return;
    const startedDate = parseDateInput(formData.startedAt);
    const durationValue = parseDurationInput(formData.actualDuration);
    if (!startedDate || durationValue === null || durationValue < 0) return;

    const completedDate = new Date(startedDate.getTime() + durationValue * HOURS_IN_MS);
    const completedValue = toLocalInputValue(completedDate);

    setFormData((prev) => {
      if (prev.completedAt === completedValue) {
        return prev;
      }
      durationDrivenUpdate.current = true;
      return { ...prev, completedAt: completedValue };
    });
  }, [formData.actualDuration, formData.startedAt, showStartedAt]);

  useEffect(() => {
    if (!showStartedAt) return;
    const startedDate = parseDateInput(formData.startedAt);
    const completedDate = parseDateInput(formData.completedAt);
    if (!startedDate || !completedDate) return;

    const diffMs = completedDate.getTime() - startedDate.getTime();
    if (!Number.isFinite(diffMs) || diffMs < 0) {
      durationDrivenUpdate.current = false;
      return;
    }

    const durationValue = formatDurationHours(diffMs / HOURS_IN_MS);

    setFormData((prev) => {
      if (durationDrivenUpdate.current) {
        durationDrivenUpdate.current = false;
        return prev;
      }
      if (prev.actualDuration === durationValue) {
        return prev;
      }
      return { ...prev, actualDuration: durationValue };
    });
  }, [formData.completedAt, formData.startedAt, showStartedAt]);

  const handleClose = () => {
    if (loading) return;
    onClose?.();
  };

  const handleSave = async () => {
    const isValid = validate();
    if (!isValid) return;

    const customErrors = {};
    const now = new Date();

    if (showComponentStart && formData.component_start_value !== "" && formData.component_start_value !== null) {
      const value = Number(formData.component_start_value);
      if (!Number.isInteger(value) || value < 0) {
        customErrors.component_start_value = "Component Starting Value must be a non-negative integer";
      }
    }

    if (
      showStartedAt &&
      formData.actualDuration !== undefined &&
      formData.actualDuration !== null &&
      formData.actualDuration !== ""
    ) {
      const durationValue = parseDurationInput(formData.actualDuration);
      if (durationValue === null || durationValue < 0) {
        customErrors.actualDuration = "Actual Duration must be a non-negative number";
      }
    }

    if (showStartedAt) {
      const startedAtDate = formData.startedAt ? new Date(formData.startedAt) : null;
      if (!startedAtDate || Number.isNaN(startedAtDate.getTime())) {
        customErrors.startedAt = "Started date/time is required";
      } else {
        const assetStartDate = component?.asset?.assetStartDate ?? component?.assetStartDate;
        const assetEndDate = component?.asset?.assetEndDate ?? component?.assetEndDate;

        if (startedAtDate > now) {
          customErrors.startedAt = "Started date/time must be in the past";
        }

        if (assetStartDate) {
          const parsedAssetStart = new Date(assetStartDate);
          if (!Number.isNaN(parsedAssetStart.getTime()) && startedAtDate < parsedAssetStart) {
            customErrors.startedAt =
              "Started date/time must be on or after the asset start date";
          }
        }

        if (assetEndDate) {
          const parsedAssetEnd = new Date(assetEndDate);
          if (!Number.isNaN(parsedAssetEnd.getTime()) && startedAtDate > parsedAssetEnd) {
            customErrors.startedAt =
              "Started date/time must be on or before the asset end date";
          }
        }
        if (showCompletedAt && formData.completedAt) {
          const completedAtDate = new Date(formData.completedAt);
          if (completedAtDate <= startedAtDate) {
            customErrors.completedAt = "Completed date/time must be after started date/time";
          }
        }
      }
    }

    if (showCompletedAt) {
      const completedAtDate = formData.completedAt ? new Date(formData.completedAt) : null;
      if (!completedAtDate || Number.isNaN(completedAtDate.getTime())) {
        customErrors.completedAt = "Completed date/time is required";
      } else {
        if (completedAtDate > now) {
          customErrors.completedAt = "Completed date/time must be in the past";
        }
        if (formData.startedAt) {
          const startedAtDate = new Date(formData.startedAt);
          if (startedAtDate >= completedAtDate) {
            customErrors.startedAt = customErrors.startedAt || "Started date/time must be before completed date/time";
          }
        }
      }
    }

    if (Object.keys(customErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...customErrors }));
      return;
    }

    setErrors((prev) => {
      const { component_start_value, startedAt, completedAt, actualDuration, ...rest } = prev;
      return rest;
    });

    const payload = {
      ...formData,
      workOrderNumber: formData.workOrderNumber || null,
      assetId: formData.assetId ?? null,
      taskId: formData.taskId ?? null,
      // teamsId: formData.teamsId ?? null,
      scheduledAt: toUtcString(formData.scheduledAt),
      dueDate: toUtcString(formData.dueDate),
      startedAt: showStartedAt ? toUtcString(formData.startedAt) : null,
      completedAt: showCompletedAt ? toUtcString(formData.completedAt) : null,
      // actualDuration: null,
      statutory: formData.statutory ? 1 : 0,
      organisationId: userProfile?.org_id ?? null,
      createdBy: userProfile?.id ?? null,
      cycleGroupOrder: formData.cycleGroupOrder ?? null,
    };

    delete payload.actualDuration;

    /* const actualDurationValue = parseDurationInput(formData.actualDuration);
    if (actualDurationValue !== null && actualDurationValue >= 0) {
      payload.actualDuration = actualDurationValue;
    } */

    payload.statusId = 4;

    if (!showStartedAt) {
      delete payload.startedAt;
    }

    if (!showCompletedAt) {
      delete payload.completedAt;
    }

    if (!showComponentStart || formData.componentStartValue === "") {
      delete payload.component_start_value;
    } else {
      const numericValue = Number(formData.componentStartValue);
      payload.componentStartValue = numericValue;
    }

    if (formData.completionImages) {
      payload.completionImages = formData.completionImages;
    }

    setLoading(true);
    try {
      if (mode === "edit" && existingWorkOrder) {
        await updateWorkOrder(existingWorkOrder.id, payload);
      } else {
        await createWorkOrder(payload);
      }
      onSaved?.();
      onClose?.();
    } catch (error) {
      console.error("Error saving work order", error);
    } finally {
      setLoading(false);
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div className="modal modal-backdrop show d-block" tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-xl" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Complete Task Using a Work Order</h5>
            <button type="button" className="btn-close" onClick={handleClose} disabled={loading} />
          </div>
          <div className="modal-body">
            <div className="row g-3">
              {workOrderFields.map((field) => renderField(field))}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReplaceComponentWorkOrderModal;
